import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  AiAssistantQuotaExceededEvent,
  AiAssistantQuotaExceededEventFromJSON,
  AIAssistantApi,
  AiAssistantSessionSummary,
  AiAssistantStep,
  AiAssistantStepTextDeltaEventFromJSON,
  AiAssistantStepTextDoneEventFromJSON,
  AiAssistantStepTextOpenedEventFromJSON,
  AiAssistantStepToolRequestedEventFromJSON,
  AiAssistantStepToolResolvedEventFromJSON,
  AiAssistantStepUserEventFromJSON,
  AiAssistantToolConfirmationRequiredEventFromJSON,
  AiAssistantTurn,
  AiAssistantTurnCancelledEventFromJSON,
  AiAssistantTurnCompletedEventFromJSON,
  AiAssistantTurnErrorEventFromJSON,
  AiAssistantUsageTokensEvent,
  AiAssistantUsageTokensEventFromJSON,
  ProblemDetail,
  SendAiAssistantSessionMessage200Response,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { PageContextService } from '@tourfold/shared-kernels/page-context';
import { AiAssistantStreamingClient } from './ai-assistant-streaming-client.service';

type AiAssistantStreamEvent = SendAiAssistantSessionMessage200Response;

/**
 * Frontend mirror of the backend turn/step model. The backend allocates `step_index` per turn and
 * includes it on every SSE event AND on every persisted step row, so the frontend is a passive
 * mirror — for each event, find the right turn by `turn_id` and the right step by `step_index`,
 * and update or insert it. There is NO client-side ordering — `step_index` IS the order.
 */
export type AiAssistantToolStatus =
  | 'requested'
  | 'awaiting_confirmation'
  | 'completed'
  | 'declined'
  | 'timeout'
  | 'error';

export type AiAssistantUiStep =
  | { kind: 'user_message'; stepIndex: number; text: string }
  | { kind: 'assistant_text'; stepIndex: number; text: string; streaming: boolean }
  | {
      kind: 'tool_call';
      stepIndex: number;
      callId: string;
      toolName: string;
      status: AiAssistantToolStatus;
    }
  | { kind: 'error'; stepIndex: number; text: string };

export interface AiAssistantUiTurn {
  id: string;
  status:
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'COMPLETED_WITH_DECLINES'
    | 'QUOTA_EXCEEDED'
    | 'DISCONNECTED'
    | 'ERROR'
    | 'CANCELLED';
  steps: AiAssistantUiStep[];
}

export interface AiAssistantPendingConfirmationItem {
  stepIndex: number;
  callId: string;
  toolName: string;
  displayArgs: Record<string, unknown> | null;
}

export interface AiAssistantPendingConfirmation {
  sessionId: string;
  turnId: string;
  confirmationId: string;
  displaySummary: string;
  items: AiAssistantPendingConfirmationItem[];
}

interface AiAssistantChatState {
  session: AiAssistantSessionSummary | null;
  sessions: AiAssistantSessionSummary[];
  turns: AiAssistantUiTurn[];
  pendingConfirmation: AiAssistantPendingConfirmation | null;
  quotaExceeded: AiAssistantQuotaExceededEvent | null;
  isHistoryOpen: boolean;
  isLoadingHistory: boolean;
  historyFailed: boolean;
  isResolvingConfirmation: boolean;
  isSending: boolean;
  isStreaming: boolean;
  sendFailed: boolean;
  /**
   * RFC 9457 problem detail for the most recent failed send. Populated from the `turn.error` SSE
   * event's `problem` field. The component dispatches on `problem.type` to a translated message
   * (e.g. `https://problems.tourfold.com/ai-assistant/assistant-disabled` →
   * `AI_ASSISTANT.ERROR.ASSISTANT_DISABLED`) and falls back to the generic translation when null
   * or when the problem type is unknown.
   */
  lastErrorProblem: ProblemDetail | null;
  usage: AiAssistantUsageTokensEvent | null;
}

const initialState: AiAssistantChatState = {
  session: null,
  sessions: [],
  turns: [],
  pendingConfirmation: null,
  quotaExceeded: null,
  isHistoryOpen: false,
  isLoadingHistory: false,
  historyFailed: false,
  isResolvingConfirmation: false,
  isSending: false,
  isStreaming: false,
  sendFailed: false,
  lastErrorProblem: null,
  usage: null,
};

export const AiAssistantChatStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    hasMessages: computed(() => store.turns().some((turn) => turn.steps.length > 0)),
    canStop: computed(() => store.isSending() || store.isStreaming()),
    /**
     * True while the model is producing the next step but no streaming text bubble is rendered
     * yet — i.e. between turn start and the first `step.text.opened`, or between a tool resolving
     * and the next text/tool step. Drives the standalone "thinking" indicator (3-dot bubble +
     * glowing Rob avatar). Suppressed while a tool confirmation is pending so the approve/decline
     * UI stands alone.
     */
    isThinking: computed(() => {
      if (!store.isSending()) {
        return false;
      }
      if (store.pendingConfirmation()) {
        return false;
      }
      const hasStreamingText = store
        .turns()
        .some((turn) =>
          turn.steps.some((step) => step.kind === 'assistant_text' && step.streaming)
        );
      return !hasStreamingText;
    }),
  })),

  withMethods((store) => {
    const aiAssistantService = inject(AIAssistantApi);
    const aiAssistantStreamingClient = inject(AiAssistantStreamingClient);
    const pageContextService = inject(PageContextService);

    // Transient handle to the in-flight stream's AbortController. Not part of the state signal —
    // it's a resource handle, not data the UI binds to.
    let streamAbort: AbortController | null = null;

    function consumeSseBuffer(buffer: string, onStreamComplete: () => void): string {
      let remaining = buffer;
      let separatorIndex = remaining.indexOf('\n\n');

      while (separatorIndex >= 0) {
        const block = remaining.slice(0, separatorIndex);
        remaining = remaining.slice(separatorIndex + 2);
        handleSseBlock(block, onStreamComplete);
        separatorIndex = remaining.indexOf('\n\n');
      }

      return remaining;
    }

    function handleSseBlock(block: string, onStreamComplete: () => void): void {
      const sse = parseSseBlock(block);
      if (!sse.data) {
        return;
      }

      let payload: AiAssistantStreamEvent | null;
      try {
        payload = parseAiAssistantStreamEvent(JSON.parse(sse.data), sse.event);
      } catch {
        return;
      }
      if (!payload) {
        return;
      }

      switch (payload.event) {
        case 'step.user':
          upsertTurnStep(payload.turnId, {
            kind: 'user_message',
            stepIndex: payload.stepIndex,
            text: payload.text,
          });
          return;
        case 'step.text.opened':
          upsertTurnStep(payload.turnId, {
            kind: 'assistant_text',
            stepIndex: payload.stepIndex,
            text: '',
            streaming: true,
          });
          patchState(store, { isStreaming: true });
          return;
        case 'step.text.delta':
          appendAssistantTextDelta(payload.turnId, payload.stepIndex, payload.delta);
          patchState(store, { isStreaming: true });
          return;
        case 'step.text.done':
          upsertTurnStep(payload.turnId, {
            kind: 'assistant_text',
            stepIndex: payload.stepIndex,
            text: payload.text,
            streaming: false,
          });
          return;
        case 'step.tool.requested':
          upsertTurnStep(payload.turnId, {
            kind: 'tool_call',
            stepIndex: payload.stepIndex,
            callId: payload.callId,
            toolName: payload.name,
            status: 'requested',
          });
          return;
        case 'tool.confirmation_required': {
          const items: AiAssistantPendingConfirmationItem[] = payload.items.map((item) => ({
            stepIndex: item.stepIndex,
            callId: item.callId,
            toolName: item.name,
            displayArgs: item.displayArgs ?? null,
          }));
          patchState(store, {
            pendingConfirmation: {
              sessionId: payload.sessionId,
              turnId: payload.turnId,
              confirmationId: payload.confirmationId,
              displaySummary: payload.displaySummary,
              items,
            },
          });
          updateToolStatusesByCallId(
            payload.turnId,
            new Set(items.map((item) => item.callId)),
            'awaiting_confirmation'
          );
          return;
        }
        case 'step.tool.resolved':
          upsertTurnStep(payload.turnId, {
            kind: 'tool_call',
            stepIndex: payload.stepIndex,
            callId: payload.callId,
            toolName: payload.name,
            status: payload.status,
          });
          clearPendingConfirmationIfResolved();
          return;
        case 'usage.tokens':
          patchState(store, { usage: payload });
          return;
        case 'quota.exceeded':
          patchState(store, { quotaExceeded: payload });
          return;
        case 'turn.completed':
          if (payload.session) {
            patchState(store, { session: payload.session });
          }
          replaceTurn(mapServerTurn(payload.turn));
          patchState(store, { pendingConfirmation: null });
          onStreamComplete();
          return;
        case 'turn.cancelled':
          markStreamingTextDone();
          onStreamComplete();
          return;
        case 'turn.error':
          patchState(store, { lastErrorProblem: payload.problem ?? null });
          onStreamComplete();
          throw new Error('AI assistant stream returned an error');
      }
    }

    function upsertTurnStep(turnId: string, step: AiAssistantUiStep): void {
      patchState(store, ({ turns }) => {
        const existing = turns.find((turn) => turn.id === turnId);
        if (!existing) {
          return {
            turns: [...turns, { id: turnId, status: 'IN_PROGRESS' as const, steps: [step] }],
          };
        }
        const stepIndex = existing.steps.findIndex(
          (candidate) => candidate.stepIndex === step.stepIndex
        );
        const nextSteps =
          stepIndex < 0
            ? insertSorted(existing.steps, step)
            : existing.steps.map((candidate, index) => (index === stepIndex ? step : candidate));
        return {
          turns: turns.map((turn) => (turn.id === turnId ? { ...turn, steps: nextSteps } : turn)),
        };
      });
    }

    function updateToolStatusesByCallId(
      turnId: string,
      callIds: Set<string>,
      status: AiAssistantToolStatus
    ): void {
      patchState(store, ({ turns }) => ({
        turns: turns.map((turn) => {
          if (turn.id !== turnId) {
            return turn;
          }
          return {
            ...turn,
            steps: turn.steps.map((step) => {
              if (step.kind !== 'tool_call' || !callIds.has(step.callId)) {
                return step;
              }
              return { ...step, status };
            }),
          };
        }),
      }));
    }

    function clearPendingConfirmationIfResolved(): void {
      const pending = store.pendingConfirmation();
      if (!pending) {
        return;
      }
      const statusesByCallId = new Map<string, AiAssistantToolStatus>();
      const pendingCallIds = new Set(pending.items.map((item) => item.callId));
      for (const turn of store.turns()) {
        if (turn.id !== pending.turnId) {
          continue;
        }
        for (const step of turn.steps) {
          if (step.kind === 'tool_call' && pendingCallIds.has(step.callId)) {
            statusesByCallId.set(step.callId, step.status);
          }
        }
      }
      const allResolved = pending.items.every((item) =>
        isResolvedToolStatus(statusesByCallId.get(item.callId))
      );
      if (allResolved) {
        patchState(store, { pendingConfirmation: null });
      }
    }

    function appendAssistantTextDelta(turnId: string, stepIndex: number, delta: string): void {
      if (!delta) {
        return;
      }
      patchState(store, ({ turns }) => ({
        turns: turns.map((turn) => {
          if (turn.id !== turnId) {
            return turn;
          }
          return {
            ...turn,
            steps: turn.steps.map((step) => {
              if (step.stepIndex !== stepIndex || step.kind !== 'assistant_text') {
                return step;
              }
              return { ...step, text: step.text + delta, streaming: true };
            }),
          };
        }),
      }));
    }

    function replaceTurn(updated: AiAssistantUiTurn): void {
      patchState(store, ({ turns }) => {
        const exists = turns.some((turn) => turn.id === updated.id);
        return {
          turns: exists
            ? turns.map((turn) => (turn.id === updated.id ? updated : turn))
            : [...turns, updated],
        };
      });
    }

    function markStreamingTextDone(): void {
      patchState(store, ({ turns }) => ({
        turns: turns.map((turn) => ({
          ...turn,
          steps: turn.steps.map((step) =>
            step.kind === 'assistant_text' && step.streaming ? { ...step, streaming: false } : step
          ),
        })),
      }));
    }

    function cancelActiveStream(): void {
      const abort = streamAbort;
      if (!abort) {
        return;
      }
      abort.abort();
      const sessionId = store.session()?.id;
      if (sessionId) {
        aiAssistantService.cancelAiAssistantActiveTurn({ sessionId }).catch(() => undefined);
      }
      if (streamAbort === abort) {
        streamAbort = null;
      }
    }

    async function ensureSession(): Promise<AiAssistantSessionSummary> {
      const currentSession = store.session();
      if (currentSession) {
        return currentSession;
      }

      const body = await aiAssistantService.createAiAssistantSession();
      patchState(store, { session: body.session });
      writeLastSessionId(body.session.id);
      return body.session;
    }

    async function streamSessionMessage(
      sessionId: string,
      content: string,
      abortSignal: AbortSignal
    ): Promise<void> {
      const response = await aiAssistantStreamingClient.streamSessionMessage(
        sessionId,
        {
          text: content,
          pageContext: pageContextService.current(),
        },
        abortSignal
      );
      await consumeSseResponse(response);
    }

    /**
     * Subscribes to the in-flight turn for [sessionId] (if any) and feeds events into the store.
     * Returns when the turn finalises or the resume stream closes. Used by [openSession] when the
     * loaded session has an IN_PROGRESS turn — typically after a browser refresh mid-stream.
     */
    async function resumeActiveTurn(sessionId: string): Promise<void> {
      if (store.isStreaming() || store.isSending()) {
        return;
      }
      const abortController = new AbortController();
      streamAbort = abortController;
      patchState(store, { isStreaming: true });
      try {
        const response = await aiAssistantStreamingClient.streamActiveTurn(
          sessionId,
          abortController.signal
        );
        await consumeSseResponse(response);
      } catch {
        // Resume is best-effort. If the network drops again the user can refresh once more.
      } finally {
        patchState(store, { isStreaming: false });
        if (streamAbort === abortController) {
          streamAbort = null;
        }
      }
    }

    async function consumeSseResponse(response: Response): Promise<void> {
      if (!response.ok) {
        throw new Error(`AI assistant stream failed with status ${response.status}`);
      }

      if (!response.body) {
        const body = await response.text();
        if (body) {
          consumeSseBuffer(`${body.replace(/\r\n/g, '\n')}\n\n`, () => undefined);
        }
        markStreamingTextDone();
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamComplete = false;
      const markStreamComplete = (): void => {
        streamComplete = true;
      };

      try {
        while (!streamComplete) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          buffer = consumeSseBuffer(
            buffer + decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n'),
            markStreamComplete
          );
        }
        if (streamComplete) {
          await reader.cancel();
        }
      } finally {
        reader.releaseLock();
      }

      markStreamingTextDone();
    }

    async function resolvePendingToolCall(action: 'confirm' | 'cancel'): Promise<void> {
      const pending = store.pendingConfirmation();
      if (!pending || store.isResolvingConfirmation()) {
        return;
      }

      patchState(store, { isResolvingConfirmation: true, sendFailed: false });
      try {
        const params = {
          sessionId: pending.sessionId,
          turnId: pending.turnId,
          confirmationId: pending.confirmationId,
        };
        await (action === 'confirm'
          ? aiAssistantService.confirmAiAssistantToolConfirmation(params)
          : aiAssistantService.cancelAiAssistantToolConfirmation(params));
      } catch {
        patchState(store, { sendFailed: true });
      } finally {
        patchState(store, { isResolvingConfirmation: false });
      }
    }

    return {
      startNewConversation(): void {
        cancelActiveStream();
        clearLastSessionId();
        patchState(store, {
          session: null,
          turns: [],
          pendingConfirmation: null,
          quotaExceeded: null,
          isHistoryOpen: false,
          usage: null,
          sendFailed: false,
          isSending: false,
          isStreaming: false,
          isResolvingConfirmation: false,
        });
      },

      /**
       * Re-opens the session the user was last in, if any. Called by the chat panel on first
       * open after a page reload so the user lands back in their conversation rather than the
       * empty greeting.
       */
      /**
       * Deletes a session from the history list. Drops the row from the local sessions signal,
       * clears `lastSessionId` if it matched (so the next page load doesn't try to restore a
       * deleted conversation), and resets the current chat to the empty state if it was the one
       * being deleted.
       */
      async deleteSession(sessionId: string): Promise<void> {
        try {
          await aiAssistantService.deleteAiAssistantSession({ sessionId });
        } catch {
          patchState(store, { historyFailed: true });
          return;
        }
        const wasCurrent = store.session()?.id === sessionId;
        if (readLastSessionId() === sessionId) {
          clearLastSessionId();
        }
        patchState(
          store,
          ({ sessions, session, turns, pendingConfirmation, quotaExceeded, usage }) => ({
            sessions: sessions.filter((candidate) => candidate.id !== sessionId),
            session: wasCurrent ? null : session,
            turns: wasCurrent ? [] : turns,
            pendingConfirmation: wasCurrent ? null : pendingConfirmation,
            quotaExceeded: wasCurrent ? null : quotaExceeded,
            usage: wasCurrent ? null : usage,
          })
        );
      },

      async restoreLastSession(): Promise<void> {
        if (store.session() || store.isLoadingHistory() || store.isSending()) {
          return;
        }
        const lastSessionId = readLastSessionId();
        if (!lastSessionId) {
          return;
        }
        // `silent: true` so a stale id (tenant wiped, retention sweep) falls back to the empty
        // greeting without showing a global error toast or flagging `historyFailed`.
        await this.openSession(lastSessionId, { silent: true });
      },

      async sendMessage(text: string): Promise<void> {
        const content = text.trim();
        if (!content || store.isSending() || store.isStreaming()) {
          return;
        }

        patchState(store, {
          pendingConfirmation: null,
          quotaExceeded: null,
          isSending: true,
          sendFailed: false,
          lastErrorProblem: null,
        });

        const abortController = new AbortController();
        streamAbort = abortController;

        try {
          const session = await ensureSession();
          await streamSessionMessage(session.id, content, abortController.signal);
        } catch (error) {
          if (!isAbortError(error)) {
            patchState(store, { sendFailed: true });
          }
          markStreamingTextDone();
        } finally {
          patchState(store, { isSending: false, isStreaming: false });
          if (streamAbort === abortController) {
            streamAbort = null;
          }
        }
      },

      stop(): void {
        cancelActiveStream();
      },

      async confirmPendingTool(): Promise<void> {
        await resolvePendingToolCall('confirm');
      },

      async cancelPendingTool(): Promise<void> {
        await resolvePendingToolCall('cancel');
      },

      async toggleHistory(): Promise<void> {
        if (store.isHistoryOpen()) {
          patchState(store, { isHistoryOpen: false });
          return;
        }
        if (store.isSending()) {
          return;
        }
        await this.openHistory();
      },

      async openHistory(): Promise<void> {
        if (store.isSending()) {
          return;
        }
        patchState(store, {
          isHistoryOpen: true,
          historyFailed: false,
          isLoadingHistory: true,
        });
        try {
          const response = await aiAssistantService.listAiAssistantSessions();
          patchState(store, { sessions: response.sessions });
        } catch {
          patchState(store, { historyFailed: true });
        } finally {
          patchState(store, { isLoadingHistory: false });
        }
      },

      /**
       * Loads a session and replays its turns. The `silent` flag is for the auto-restore path:
       * when the persisted `lastSessionId` points at a session that no longer exists (tenant
       * data wiped, manual cleanup, retention sweep) we want to fall back to a new chat without
       * surfacing the global 404 toast and without leaving `historyFailed` flagged. User-driven
       * history clicks keep the default behaviour.
       */
      async openSession(sessionId: string, options: { silent?: boolean } = {}): Promise<void> {
        if (store.isSending()) {
          return;
        }
        const silent = options.silent === true;

        patchState(store, { historyFailed: false, isLoadingHistory: true });
        try {
          const response = await aiAssistantService.getAiAssistantSession({ sessionId });
          patchState(store, {
            session: response.session,
            turns: response.turns.map(mapServerTurn),
            pendingConfirmation: null,
            quotaExceeded: null,
            sendFailed: false,
            isHistoryOpen: false,
          });
          writeLastSessionId(response.session.id);
          // If a turn was running when the previous session ended (page refresh, dropped SSE),
          // pick it back up — the backend kept persisting steps and will keep streaming.
          const hasActiveTurn = response.turns.some((turn) => turn.outcome === 'IN_PROGRESS');
          if (hasActiveTurn) {
            void resumeActiveTurn(sessionId);
          }
        } catch {
          if (silent) {
            // Stale lastSessionId — fall back to the empty greeting and forget the dead id so the
            // next reload doesn't try again.
            clearLastSessionId();
          } else {
            patchState(store, { historyFailed: true });
          }
        } finally {
          patchState(store, { isLoadingHistory: false });
        }
      },
    };
  }),

  withHooks({
    onInit(store) {
      // First time the chat panel is opened after a page load, drop the user back into the
      // session they were in (preserved via localStorage). They can opt out by clicking
      // "New chat", which clears the stored id.
      store.restoreLastSession().catch(() => undefined);
    },
  })
);

const LAST_SESSION_KEY = 'tourfold.aiAssistant.lastSessionId';

function readLastSessionId(): string | null {
  try {
    return globalThis.localStorage?.getItem(LAST_SESSION_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeLastSessionId(sessionId: string): void {
  try {
    globalThis.localStorage?.setItem(LAST_SESSION_KEY, sessionId);
  } catch {
    // localStorage may be unavailable (private mode, etc.) — silently no-op.
  }
}

function clearLastSessionId(): void {
  try {
    globalThis.localStorage?.removeItem(LAST_SESSION_KEY);
  } catch {
    // localStorage may be unavailable (private mode, etc.) — silently no-op.
  }
}

/**
 * Compact "time ago" string in Claude-style — `2m`, `3h`, `4d`, `5w`, `2y`. Same suffixes work for
 * both en and de UIs (we treat them as universally recognised compact units rather than
 * translating "minutes / Minuten"). Pure function so the component can call it from a `track`
 * expression cheaply.
 */
export function formatRelativeTimestamp(
  input: string | Date | null | undefined,
  now: Date = new Date()
): string {
  if (!input) return '';
  const date = typeof input === 'string' ? new Date(input) : input;
  const seconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 52) return `${weeks}w`;
  const years = Math.floor(days / 365);
  return `${years}y`;
}

function insertSorted(steps: AiAssistantUiStep[], step: AiAssistantUiStep): AiAssistantUiStep[] {
  const insertAt = steps.findIndex((candidate) => candidate.stepIndex > step.stepIndex);
  if (insertAt < 0) {
    return [...steps, step];
  }
  const next = steps.slice();
  next.splice(insertAt, 0, step);
  return next;
}

function mapServerTurn(turn: AiAssistantTurn): AiAssistantUiTurn {
  return {
    id: turn.id,
    status: turn.outcome,
    steps: turn.steps.map(mapServerStep).sort((a, b) => a.stepIndex - b.stepIndex),
  };
}

function mapServerStep(step: AiAssistantStep): AiAssistantUiStep {
  switch (step.kind) {
    case 'user_message':
      return { kind: 'user_message', stepIndex: step.stepIndex, text: step.text ?? '' };
    case 'assistant_text':
      return {
        kind: 'assistant_text',
        stepIndex: step.stepIndex,
        text: step.text ?? '',
        streaming: false,
      };
    case 'tool_call':
      return {
        kind: 'tool_call',
        stepIndex: step.stepIndex,
        callId: step.callId ?? '',
        toolName: step.toolName ?? '',
        status: (step.toolStatus ?? 'requested') as AiAssistantToolStatus,
      };
    case 'error':
      return { kind: 'error', stepIndex: step.stepIndex, text: step.text ?? '' };
  }
}

function parseAiAssistantStreamEvent(
  json: unknown,
  sseEvent: string
): AiAssistantStreamEvent | null {
  if (!isRecord(json)) {
    return null;
  }
  const event = typeof json['event'] === 'string' ? json['event'] : sseEvent;
  const payload = json['event'] === event ? json : { ...json, event };

  switch (event) {
    case 'step.user':
      return AiAssistantStepUserEventFromJSON(payload);
    case 'step.text.opened':
      return AiAssistantStepTextOpenedEventFromJSON(payload);
    case 'step.text.delta':
      return AiAssistantStepTextDeltaEventFromJSON(payload);
    case 'step.text.done':
      return AiAssistantStepTextDoneEventFromJSON(payload);
    case 'step.tool.requested':
      return AiAssistantStepToolRequestedEventFromJSON(payload);
    case 'tool.confirmation_required':
      return AiAssistantToolConfirmationRequiredEventFromJSON(payload);
    case 'step.tool.resolved':
      return AiAssistantStepToolResolvedEventFromJSON(payload);
    case 'usage.tokens':
      return AiAssistantUsageTokensEventFromJSON(payload);
    case 'quota.exceeded':
      return AiAssistantQuotaExceededEventFromJSON(payload);
    case 'turn.completed':
      return AiAssistantTurnCompletedEventFromJSON(payload);
    case 'turn.cancelled':
      return AiAssistantTurnCancelledEventFromJSON(payload);
    case 'turn.error':
      return AiAssistantTurnErrorEventFromJSON(payload);
    default:
      return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseSseBlock(block: string): { event: string; data: string } {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
    }
  }

  return { event, data: dataLines.join('\n') };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function isResolvedToolStatus(status: AiAssistantToolStatus | undefined): boolean {
  return (
    status === 'completed' || status === 'declined' || status === 'timeout' || status === 'error'
  );
}
